import { useState } from 'react';
import type { TUploadedFile } from '@/shared/types';
import { uploadFilesToBishop } from '@/shared/utils/file-upload';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

/**
 * Configuration options for useFileUpload hook.
 */
export interface IUseFileUploadConfig {
  /** Callback when files are successfully uploaded */
  onSuccess?: (uploadedFiles: TUploadedFile[]) => void;
  /** Callback when an error occurs during upload */
  onError?: (error: Error) => void;
}

/**
 * Return type for useFileUpload hook.
 */
export interface IUseFileUploadReturn {
  /** Function to upload files to Bishop API */
  uploadFiles: (files: File[]) => Promise<TUploadedFile[]>;
  /** Whether files are currently being uploaded */
  isUploading: boolean;
}

/**
 * Shared hook for uploading files to Bishop API.
 *
 * Reads the current organization and workspace from the global store,
 * uploads files via the workspace-scoped Bishop endpoint, and provides
 * loading state plus optional success/error callbacks.
 *
 * @param config - Optional configuration with success/error callbacks
 * @returns Upload function and loading state
 *
 * @example
 * ```tsx
 * const { uploadFiles, isUploading } = useFileUpload({
 *   onSuccess: (files) => console.log('Uploaded:', files),
 *   onError: (error) => toast.error('Upload failed'),
 * });
 *
 * const handleSend = async () => {
 *   const uploaded = await uploadFiles(selectedFiles);
 *   sendMessage(content, uploaded);
 * };
 * ```
 */
export function useFileUpload(
  config?: IUseFileUploadConfig,
): IUseFileUploadReturn {
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Uploads multiple files to Bishop API.
   *
   * @param files - Array of files to upload
   * @returns Promise resolving to array of uploaded file metadata
   */
  const uploadFiles = async (files: File[]): Promise<TUploadedFile[]> => {
    if (!files || files.length === 0) return [];

    setIsUploading(true);

    try {
      const uploadedFiles = await uploadFilesToBishop(
        files,
        currentOrganizationId,
        currentWorkspaceId,
      );
      config?.onSuccess?.(uploadedFiles);
      return uploadedFiles;
    } catch (error) {
      config?.onError?.(error as Error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFiles,
    isUploading,
  };
}
